// Settings schema, defaults and limits. Mirrors docs/ARCHITECTURE.md "Settings schema".

export type Action =
  | "forward"
  | "back"
  | "left"
  | "right"
  | "jump"
  | "crouch"
  | "walk"
  | "fire"
  | "ads"
  | "reload"
  | "interact"
  | "melee"
  | "lethal"
  | "tactical"
  | "primary"
  | "secondary"
  | "knife"
  | "nextWeapon"
  | "prevWeapon"
  | "lastWeapon"
  | "scoreboard"
  | "chat"
  | "teamChat"
  | "lobby"
  | "dropBomb";

/** KeyboardEvent.code, or "Mouse0".."Mouse4" (MouseEvent.button), "WheelUp", "WheelDown". */
export type Binding = string;

/** "vsync" and "unlimited" render every animation frame; a number caps the frame rate (settings/frameLimiter.ts). */
export const FRAME_LIMITERS = ["vsync", "unlimited", "60", "120", "144", "165", "240", "360"] as const;
export type FrameLimiter = (typeof FRAME_LIMITERS)[number];

export interface Settings {
  version: 1;
  controls: {
    /** CS-equivalent: degrees per count = sensitivity * 0.022. */
    sensitivity: number;
    /** Multiplier on top of the zoom-ratio scaled sensitivity while aiming down sights (`degreesPerCount`). */
    adsSensitivityMult: number;
    /** Multiplier on top of the zoom-ratio scaled sensitivity behind a scope overlay (sniper, 4x). */
    scopeSensitivityMult: number;
    invertY: boolean;
    rawInput: boolean;
    toggleAds: boolean;
    toggleCrouch: boolean;
    /** Every mouse wheel notch is a fresh jump press; wheel bindings (weapon switching) are inactive. */
    jumpOnWheel: boolean;
  };
  /** Up to 2 bindings per action. An empty array means unbound. */
  keybinds: Record<Action, Binding[]>;
  video: {
    /** Horizontal FOV in degrees at 16:9, 80..120. */
    fov: number;
    viewmodelFov: number;
    /** Internal resolution scale, 0.5..1.5. */
    renderScale: number;
    quality: "low" | "medium" | "high" | "ultra";
    shadows: boolean;
    bloom: boolean;
    filmGrain: boolean;
    vignette: boolean;
    antialias: "off" | "fxaa";
    frameLimiter: FrameLimiter;
    dynamicFov: boolean;
    showViewmodel: boolean;
  };
  /** Volumes are linear 0..1. */
  audio: {
    master: number;
    sfx: number;
    ui: number;
    music: number;
    hitsound: "classic" | "tick" | "off";
    killsound: boolean;
  };
  /**
   * Geometry (all CSS px at hud.scale 1, drawn by `drawCrosshair` in settings/crosshair.ts):
   *  - cross:    four arms, each `size` long and `thickness` thick, starting `gap` from the center
   *  - dot:      filled dot, diameter `thickness * 2`
   *  - circle:   ring of radius `gap + size`, stroke `thickness`
   *  - crossdot: cross plus the dot
   * `outline` adds a dark `outlineThickness` border around every shape. `dynamic` lets the HUD
   * widen `gap` with the current spread.
   */
  crosshair: {
    style: "cross" | "dot" | "circle" | "crossdot";
    /** "#rrggbb" */
    color: string;
    size: number;
    gap: number;
    thickness: number;
    outline: boolean;
    outlineThickness: number;
    opacity: number;
    dynamic: boolean;
    /** Colour of the reticle dot while aiming through a red dot or holo: classic red, or the crosshair colour. */
    dotColor: "red" | "crosshair";
  };
  hud: {
    scale: number;
    showFps: boolean;
    showPing: boolean;
    showNetGraph: boolean;
    showSpeed: boolean;
    hitmarkers: boolean;
  };
  network: {
    transport: "auto" | "webtransport" | "websocket";
    interpolation: "auto" | "low" | "high";
  };
}

export const SETTINGS_VERSION = 1;
export const SETTINGS_KEY = "gunrun.settings.v1";
export const MAX_BINDINGS_PER_ACTION = 2;

export type SettingsSection = Exclude<keyof Settings, "version" | "keybinds">;

// ---- Actions ----

export type ActionGroupId = "movement" | "combat" | "weapons" | "match";

export interface ActionGroup {
  id: ActionGroupId;
  label: string;
  actions: readonly Action[];
}

/** Display order of the keybind list. */
export const ACTION_GROUPS: readonly ActionGroup[] = [
  {
    id: "movement",
    label: "Movement",
    actions: ["forward", "back", "left", "right", "jump", "crouch", "walk"],
  },
  {
    id: "combat",
    label: "Combat",
    actions: [
      "fire",
      "ads",
      "reload",
      "melee",
      "lethal",
      "tactical",
      "interact",
    ],
  },
  {
    id: "weapons",
    label: "Weapons",
    actions: [
      "primary",
      "secondary",
      "knife",
      "nextWeapon",
      "prevWeapon",
      "lastWeapon",
    ],
  },
  {
    id: "match",
    label: "Match",
    actions: ["scoreboard", "chat", "teamChat", "lobby", "dropBomb"],
  },
];

export const ACTIONS: readonly Action[] = ACTION_GROUPS.flatMap(
  (g) => g.actions,
);

export const ACTION_LABELS: Readonly<Record<Action, string>> = {
  forward: "Move forward",
  back: "Move back",
  left: "Move left",
  right: "Move right",
  jump: "Jump",
  crouch: "Crouch / slide",
  walk: "Walk",
  fire: "Fire",
  ads: "Aim down sights",
  reload: "Reload",
  interact: "Interact (plant / defuse)",
  melee: "Quick melee",
  lethal: "Lethal equipment",
  tactical: "Tactical equipment",
  primary: "Primary weapon",
  secondary: "Secondary weapon",
  knife: "Knife",
  nextWeapon: "Next weapon",
  prevWeapon: "Previous weapon",
  lastWeapon: "Last weapon",
  scoreboard: "Scoreboard",
  chat: "Chat",
  teamChat: "Team chat",
  lobby: "Lobby panel (custom lobbies)",
  dropBomb: "Drop bomb",
};

export const DEFAULT_KEYBINDS: Readonly<Record<Action, readonly Binding[]>> = {
  forward: ["KeyW"],
  back: ["KeyS"],
  left: ["KeyA"],
  right: ["KeyD"],
  jump: ["Space"],
  crouch: ["ShiftLeft", "KeyC"],
  walk: ["AltLeft"],
  fire: ["Mouse0"],
  ads: ["Mouse2"],
  reload: ["KeyR"],
  interact: ["KeyE"],
  melee: ["KeyV"],
  lethal: ["KeyG"],
  tactical: ["KeyF"],
  primary: ["Digit1"],
  secondary: ["Digit2"],
  knife: ["Digit3"],
  nextWeapon: ["WheelDown"],
  prevWeapon: ["WheelUp"],
  lastWeapon: ["KeyX"],
  scoreboard: ["Tab"],
  chat: ["KeyT"],
  teamChat: ["KeyY"],
  lobby: ["KeyL"],
  dropBomb: ["KeyH"],
};

export function defaultKeybinds(): Record<Action, Binding[]> {
  const out = {} as Record<Action, Binding[]>;
  for (const a of ACTIONS) out[a] = [...DEFAULT_KEYBINDS[a]];
  return out;
}

// ---- Numeric ranges (used by sanitizing merge and by the sliders) ----

export interface Range {
  min: number;
  max: number;
  step: number;
}

export const RANGES = {
  sensitivity: { min: 0.05, max: 10, step: 0.01 },
  adsSensitivityMult: { min: 0.1, max: 3, step: 0.01 },
  scopeSensitivityMult: { min: 0.1, max: 3, step: 0.01 },
  fov: { min: 80, max: 120, step: 1 },
  viewmodelFov: { min: 50, max: 90, step: 1 },
  renderScale: { min: 0.5, max: 1.5, step: 0.05 },
  volume: { min: 0, max: 1, step: 0.01 },
  crosshairSize: { min: 0, max: 40, step: 1 },
  crosshairGap: { min: 0, max: 30, step: 1 },
  crosshairThickness: { min: 1, max: 10, step: 1 },
  crosshairOutlineThickness: { min: 0, max: 4, step: 1 },
  crosshairOpacity: { min: 0.1, max: 1, step: 0.05 },
  hudScale: { min: 0.6, max: 1.6, step: 0.05 },
} as const satisfies Record<string, Range>;

// ---- Field specs (drive the sanitizing merge in persist.ts) ----

export type FieldSpec =
  | { kind: "number"; min: number; max: number }
  | { kind: "boolean" }
  | { kind: "enum"; values: readonly string[] }
  | { kind: "color" };

const num = (r: Range): FieldSpec => ({
  kind: "number",
  min: r.min,
  max: r.max,
});
const bool: FieldSpec = { kind: "boolean" };
const oneOf = (...values: string[]): FieldSpec => ({ kind: "enum", values });

type SectionSpecs = {
  [S in SettingsSection]: Record<keyof Settings[S], FieldSpec>;
};

export const SECTION_SPECS: SectionSpecs = {
  controls: {
    sensitivity: num(RANGES.sensitivity),
    adsSensitivityMult: num(RANGES.adsSensitivityMult),
    scopeSensitivityMult: num(RANGES.scopeSensitivityMult),
    invertY: bool,
    rawInput: bool,
    toggleAds: bool,
    toggleCrouch: bool,
    jumpOnWheel: bool,
  },
  video: {
    fov: num(RANGES.fov),
    viewmodelFov: num(RANGES.viewmodelFov),
    renderScale: num(RANGES.renderScale),
    quality: oneOf("low", "medium", "high", "ultra"),
    shadows: bool,
    bloom: bool,
    filmGrain: bool,
    vignette: bool,
    antialias: oneOf("off", "fxaa"),
    frameLimiter: oneOf(...FRAME_LIMITERS),
    dynamicFov: bool,
    showViewmodel: bool,
  },
  audio: {
    master: num(RANGES.volume),
    sfx: num(RANGES.volume),
    ui: num(RANGES.volume),
    music: num(RANGES.volume),
    hitsound: oneOf("classic", "tick", "off"),
    killsound: bool,
  },
  crosshair: {
    style: oneOf("cross", "dot", "circle", "crossdot"),
    color: { kind: "color" },
    size: num(RANGES.crosshairSize),
    gap: num(RANGES.crosshairGap),
    thickness: num(RANGES.crosshairThickness),
    outline: bool,
    outlineThickness: num(RANGES.crosshairOutlineThickness),
    opacity: num(RANGES.crosshairOpacity),
    dynamic: bool,
    dotColor: oneOf("red", "crosshair"),
  },
  hud: {
    scale: num(RANGES.hudScale),
    showFps: bool,
    showPing: bool,
    showNetGraph: bool,
    showSpeed: bool,
    hitmarkers: bool,
  },
  network: {
    transport: oneOf("auto", "webtransport", "websocket"),
    interpolation: oneOf("auto", "low", "high"),
  },
};

/** A fresh copy of the shipped defaults. */
export function defaultSettings(): Settings {
  return {
    version: 1,
    controls: {
      sensitivity: 1.2,
      adsSensitivityMult: 1,
      scopeSensitivityMult: 1,
      invertY: false,
      rawInput: true,
      toggleAds: false,
      toggleCrouch: false,
      jumpOnWheel: false,
    },
    keybinds: defaultKeybinds(),
    video: {
      fov: 103,
      viewmodelFov: 68,
      renderScale: 1,
      quality: "high",
      shadows: true,
      bloom: true,
      filmGrain: false,
      vignette: false,
      antialias: "fxaa",
      frameLimiter: "vsync",
      dynamicFov: true,
      showViewmodel: true,
    },
    audio: {
      master: 0.8,
      sfx: 1,
      ui: 0.8,
      music: 0.5,
      hitsound: "classic",
      killsound: true,
    },
    crosshair: {
      style: "cross",
      color: "#ffffff",
      size: 8,
      gap: 4,
      thickness: 2,
      outline: true,
      outlineThickness: 1,
      opacity: 1,
      dynamic: true,
      dotColor: "red",
    },
    hud: {
      scale: 1,
      showFps: false,
      showPing: true,
      showNetGraph: false,
      showSpeed: false,
      hitmarkers: true,
    },
    network: {
      transport: "auto",
      interpolation: "auto",
    },
  };
}
